import { DeleteAttachmentRequest, IAttachment, IAttachmentExtendedRecord } from "@shared/mirage/core/contracts/IAttachment";
import _ from "lodash";
import { CopyAction } from "./CopyAction";
import { DeleteAction } from "./DeleteAction";
import { DownloadAction } from "./DownloadAction";
import { ReadAction } from "./ReadAction";
import { SupersedeAction } from "./SupersedeAction";
import { MeasureAndRedactAction } from "./ViewAction";
import { SetStateAction } from "react";
import LocalAttachmentsService from "@mirage/services/LocalAttachmentsService";
import { useDownloadAttachment } from "@mirage/hooks/useDownloadAttachment";
import { useConfirmDialog } from "@mirage/hooks/useConfirmDialog";
import { Typography } from "@mui/material";
import useAlertService from "@mirage/hooks/useAlertService";
import { SplitAction } from "./SplitAction";
import { GridRowId, GridValidRowModel } from "@mui/x-data-grid-premium";
import { ImageViewAction } from "./ImageViewAction";
import { useImageViewer } from "@mirage/hooks/useImageViewer";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";

export interface DocumentsTableActionCellProps {
    row: IAttachment;
    publicOnly: boolean;
    setRequestedPdfUrl: React.Dispatch<SetStateAction<string>>;
    onSupersedeClicked?: (att: IAttachment) => void;
    service: LocalAttachmentsService;
    onListChanged: () => void;
    readOnly: boolean;
    getSelectedRows?: () => Map<GridRowId, GridValidRowModel>;
    overrideActions?: (actions: JSX.Element[], row: any & IAttachment) => JSX.Element[];
}
export const DocumentsTableActionCell = (props: DocumentsTableActionCellProps) => {
    const { getSelectedRows, row, readOnly, publicOnly, setRequestedPdfUrl, onSupersedeClicked, service, onListChanged, overrideActions } = props;
    const [download] = useDownloadAttachment(service);
    const [showDialog, isConfirmed] = useConfirmDialog();
    const showAlert = useAlertService();
    const { show: showImage } = useImageViewer();
    const att: IAttachment = row as IAttachment;
    const isAccessible = !publicOnly || att.access === "Public";
    const downLoadAction = isAccessible ? <DownloadAction key="download" onDownload={download} attachmentId={att.id} /> : null;
    const copyAction = isAccessible ? <CopyAction key="copy" onDownload={download} attachmentId={att.id} /> : null;
    const originalFileName = att.originalFileName || att.name;
    const extension = "." + originalFileName.split(".").pop()?.toLowerCase();
    const isPdf = _.endsWith(originalFileName.toLowerCase(), ".pdf");
    const isImage = [".png", ".jpg", ".jpeg", ".gif"].indexOf(extension) >= 0;
    const readAction = isPdf ? (
        <ReadAction key="read" onDownload={async () => service.DownloadAttachment(att.id)} attachmentId={att.id} setRequestedPdfUrl={setRequestedPdfUrl} />
    ) : null;
    const pdfViewAction = isPdf ? (
        <MeasureAndRedactAction
            key="measure"
            attachmentId={att.id}
            onClosed={() => {
                onListChanged();
            }}
        />
    ) : null;
    const imageViewAction = isImage ? (
        <ImageViewAction
            key="image"
            onView={async () => {
                await onImageView();
            }}
        />
    ) : null;
    const supersedeAction = !publicOnly && !readOnly && onSupersedeClicked && !row.readOnly ? <SupersedeAction key="supersede" onClick={() => onSupersedeClicked(att)} /> : null;
    const deleteAction = !publicOnly && !readOnly && !row.readOnly ? <DeleteAction key="delete" onClick={async () => await onDeleteClicked(att)} /> : null;
    const splitAction = !publicOnly && !readOnly && isPdf ? <SplitAction key="split" onClick={async () => await onSplitClicked(att)} /> : null;
    const baseActions = [deleteAction, supersedeAction, downLoadAction, copyAction, splitAction, pdfViewAction, readAction, imageViewAction].filter(Boolean) as JSX.Element[];
    const actions = [];
    if (overrideActions) {
        actions.push(...overrideActions(baseActions, row));
    } else {
        actions.push(...baseActions);
    }
    return (
        <span>
            {actions.map((action, index) => {
                return (
                    <span key={index} className="documentsTable__action">
                        {action}
                    </span>
                );
            })}
        </span>
    );
    async function onImageView() {
        const extension = att.name.split(".").pop()?.toLowerCase();
        const resp = await service.DownloadAttachmentBody(att.id);
        if (resp) {
            const extendedAttachment = att as IAttachmentExtendedRecord;
            const base64 = `data:image/${extension};base64,` + resp;
            const result = await showImage(base64, {
                creator: extendedAttachment.createdByUserId_relatedRecord?.name || "",
                description: att.description,
                lastModified: DateFormatter.formatToDateTimeAmPm(att.lastModifiedTime),
                name: att.name,
                size: (att.bodyLength / 1024).toFixed(2) + "Kb",
                readonly: readOnly,
            });
            if (result.description) {
                att.description = result.description;
                await service.UpdateAttachments([att]);
                onListChanged();
            }
        }
    }
    function getDeleteConfirmationDialogContent(attachments: IAttachment[]) {
        const plural = attachments.length > 1 ? "s" : "";
        const message = `Delete attachment${plural} ${attachments.map((r) => `${r.name}`).join(",\n")} ?`;
        return (
            <>
                <Typography className="documentsTable__typography--breakLines" fontWeight={600}>
                    {message}
                </Typography>
                <Typography>You can't undo the action.</Typography>
            </>
        );
    }
    function getDeleteHistoryConfirmationDialogContent(att: IAttachment) {
        return (
            <>
                <Typography
                    className="documentsTable__typography--breakLines"
                    fontWeight={600}
                >{`Would you like to delete an entire history of the attachment ${att.name}?`}</Typography>
            </>
        );
    }
    async function onDeleteClicked(att: IAttachment) {
        let rangeToDelete = [att];
        if (typeof getSelectedRows === "function") {
            const rows = getSelectedRows();
            let selectedAttachments = Array.from(rows.values()) as IAttachment[];
            const index = selectedAttachments.findIndex((r) => !!r && r.id == att.id);
            if (index < 0) {
                selectedAttachments = [att];
            }
            rangeToDelete = selectedAttachments;
        }
        showDialog({ mutation: getDeleteConfirmationDialogContent(rangeToDelete), onConfirmLabel: "Delete" });
        const confirmed = await isConfirmed();
        if (confirmed) {
            for (const attachment of rangeToDelete) {
                const request: DeleteAttachmentRequest = { attachmentId: attachment.id };
                if (attachment.history && attachment.history.length > 0) {
                    showDialog({ mutation: getDeleteHistoryConfirmationDialogContent(attachment), onConfirmLabel: "Delete" });
                    const deleteHistoryConfirmed = await isConfirmed();
                    if (deleteHistoryConfirmed) {
                        request.deleteHistory = attachment.history.map((r) => r.id);
                    } else {
                        request.pickUpId = attachment.history[0].id;
                    }
                }
                await service.DeleteAttachment(request);
            }
            onListChanged();
        }
    }
    async function onSplitClicked(att: IAttachment) {
        const resp = await service.SplitAttachment(att.id);
        if (resp.errorMsg) {
            showAlert(resp.errorMsg, "error");
        } else {
            onListChanged();
        }
    }
};
